import { useTranslation } from 'react-i18next';
import type { TournamentStatus } from '@pokerroom/types';

/** Display helpers shared by the tournament list and detail pages. */

// Square status labels (ZIPA COMPONENTS.md §4): the word carries the meaning, colour supports it.
const STATUS_BADGES: Record<TournamentStatus, string> = {
  DRAFT: 'bg-base-300 text-base-content',
  REGISTERED: 'bg-info/10 text-info',
  IN_PROGRESS: 'bg-success/10 text-success',
  PAUSED: 'border border-warning text-base-content',
  COMPLETED: 'bg-primary/10 text-primary',
  CANCELLED: 'border border-error text-base-content',
};

export function TournamentStatusBadge({ status }: { status: TournamentStatus }) {
  const { t } = useTranslation('tournament');
  return (
    <span
      className={`inline-flex h-6 items-center px-2 text-xs font-medium shrink-0 ${STATUS_BADGES[status]}`}
    >
      {t(`status.${status}`)}
    </span>
  );
}

// Decimal amounts arrive as strings. Group the integer digits in the user's locale and keep the
// fraction digits as received, so no value is rounded or converted; anything else is shown raw.
export function formatAmount(value: string, locale: string): string {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return value;
  const [, sign = '', whole = '0', fraction] = match;
  const grouped = BigInt(whole).toLocaleString(locale);
  if (fraction === undefined) return `${sign}${grouped}`;
  const decimal =
    new Intl.NumberFormat(locale).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ??
    '.';
  return `${sign}${grouped}${decimal}${fraction}`;
}
